import { z } from 'zod';
import { operatorApiFetch } from './apiService';
import { apiSuccess, apiList, parseApiResponse, type JsonObject } from '@services/apiSchemas';
import {
  InventoryTransformationStatusSchema,
  InventoryTransformationItemStatusSchema,
  RuleSnapshotSchema,
  ReasonCodeSchema,
  FailureInterpolationsSchema,
} from '@services/inventoryTransformationSchemas';

// Schemas mirror Api::Operator::V1::InventoryTransformationsController
// (app/controllers/api/operator/v1/inventory_transformations_controller.rb).
//
// Enum / snapshot / reason_code schemas come from the shared module — they
// mirror Rails source-of-truth (model enums, REASON_CODES constant,
// SnapshotBuilder structure). Surface-specific row/detail shapes remain local:
// operator show emits more item fields (source_inventory_id, shelf_id,
// product_id, shelf_location, source/target_inventory_type_id) used by the
// operator Edit/Show pages; the list row carries `applicant_kind` instead of
// `organization_name` / `warehouse_name`.
//
// All object schemas use .passthrough() so backend additions don't break
// parsing — drift detection comes from missing or mistyped fields, not extras.

export { InventoryTransformationStatusSchema, InventoryTransformationItemStatusSchema };
export type InventoryTransformationStatus = z.infer<typeof InventoryTransformationStatusSchema>;
export type InventoryTransformationItemStatus = z.infer<typeof InventoryTransformationItemStatusSchema>;

// Row shape returned by POST get_records (controller line 35-47). Operator
// projection omits org/warehouse name (operator only sees their own) but adds
// `applicant_kind` so the FE can render an icon distinguishing merchant_user
// vs operator without a second roundtrip.
const InventoryTransformationRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  kind: z.string(),
  status: InventoryTransformationStatusSchema,
  note: z.string().nullable(),
  applicant_name: z.string().nullable(),
  applicant_kind: z.enum([ 'merchant_user', 'operator' ]),
  approver_name: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// Applicant / approver sub-object on the detail payload.
// applicant (line 62-66): always emitted as an object, but id/name can be null
// when the underlying applicant association is nil (legacy data); kind is
// always derived. Modeled as nullable fields rather than a nullable object.
// approver (line 67-70): the whole object is nil when not yet approved/rejected.
const ApplicantRefSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  kind: z.enum([ 'merchant_user', 'operator' ]),
}).passthrough();

const ApproverRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
}).passthrough();

// Per-item shape on the detail payload (controller line 71-99). Source-side
// fields are nullable because source_inventory can be nil (items that failed
// before/without an inventory binding); target-side fields are nullable for
// purpose_reallocation rows (no batch / expiration override on the target).
const InventoryTransformationItemSchema = z.object({
  id: z.string(),
  source_inventory_id: z.string().nullable(),
  shelf_id: z.string(),
  product_id: z.string(),
  product_name: z.string(),
  product_sku: z.string(),
  quantity: z.number(),
  reason_code: ReasonCodeSchema,
  shelf_location: z.string(),
  source_batch: z.string().nullable(),
  target_batch: z.string().nullable(),
  source_expiration_date: z.string().nullable(),
  target_expiration_date: z.string().nullable(),
  source_manufacturing_date: z.string().nullable(),
  target_manufacturing_date: z.string().nullable(),
  source_inventory_type_id: z.string().nullable(),
  source_inventory_type_name: z.string().nullable(),
  target_inventory_type_id: z.string().nullable(),
  target_inventory_type_name: z.string().nullable(),
  source_custom_attributes: z.record(z.string(), z.unknown()),
  target_custom_attributes: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
  // New in partial-success spec ↓
  status: InventoryTransformationItemStatusSchema,
  failure_code: z.string().nullable(),
  failure_interpolations: FailureInterpolationsSchema,
  executed_at: z.string().nullable(),
}).passthrough();
export type InventoryTransformationItem = z.infer<typeof InventoryTransformationItemSchema>;

// Detail payload from GET /:id (controller line 51-105). Includes the new
// applied_count / failed_count / total_items_count rollups computed in-memory
// from the eager-loaded items association.
const InventoryTransformationDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  kind: z.string(),
  status: InventoryTransformationStatusSchema,
  note: z.string().nullable(),
  // Canonical merchant-scope payload: top-level merchant_id + nested merchant
  // (id + name) used by FE Edit wrapper (useRecordMerchantSync) to align
  // OperatorDataContext.currentMerchant to the record's merchant before any
  // merchant-scoped dropdown / reference data fetch happens.
  merchant_id: z.string(),
  merchant: z.object({ id: z.string(), name: z.string() }).passthrough(),
  created_at: z.string(),
  triggered_by_rule_snapshot: RuleSnapshotSchema.nullable(),
  applicant: ApplicantRefSchema,
  approver: ApproverRefSchema.nullable(),
  items: z.array(InventoryTransformationItemSchema),
  applied_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  total_items_count: z.number().int().nonnegative(),
}).passthrough();
export type InventoryTransformationDetail = z.infer<typeof InventoryTransformationDetailSchema>;

// suggest_name returns { name } (controller line 13-16). `name` could in
// theory be empty/null in future "no suggestion" cases; modeled as nullable
// for forward-compat.
const SuggestNameSchema = z.object({
  name: z.string().nullable(),
}).passthrough();

// create returns only { id } on operator (controller line 153). Unlike admin,
// the operator flow always lands in `pending` and the partial-success counters
// don't apply until approve runs — so no status / applied_count / failed_count
// echoed back here. The New page navigates to /:id which re-fetches via show.
const InventoryTransformationCreatedSchema = z.object({
  id: z.string(),
}).passthrough();

// approve / reject / update all call bare `render_success` (controller line
// 161, 168, 175) → envelope is { success: true, message: "", data: null }.
// We don't parse `data` (it's always null); callers only branch on `success`
// and surface `message` from error envelopes.
const SuccessEnvelopeSchema = z.object({
  success: z.literal(true),
  message: z.string(),
  data: z.null(),
}).passthrough();

// Create payload mirrors the operator create flow (controller line 108-155).
// shelf_id is required on operator side (each item must be tied to a specific
// shelf), unlike admin which derives it from the inventory.
//
// `extends JsonObject` 讓 operatorApiFetch body 接得到。JsonObject 嚴格不含 `undefined`
// （JSON spec 沒這個 value），所以 optional field 一律寫 `T | null`（required + nullable），
// caller 在 construction 時用 `?? null` 顯式表態。混 `?:` 會回到 `undefined` 漏出來、
// 滿足不了 JsonObject 的 index sig contract。
//
// `target_custom_attributes` 是 jsonb 欄位在 body 內的形態，型別用 `JsonObject`
// （JSON-safe nested 物件）對齊 BE。
export interface InventoryTransformationCreateItem extends JsonObject {
  inventory_id: string;
  shelf_id: string;
  quantity: number;
  reason_code: string;
  target_inventory_type_id: string | null;
  target_batch: string | null;
  target_expiration_date: string | null;
  target_manufacturing_date: string | null;
  target_custom_attributes: JsonObject | null;
  note: string | null;
}

export interface InventoryTransformationCreateInput extends JsonObject {
  name: string | null;
  note: string | null;
  merchant_id: string | null;
  items: InventoryTransformationCreateItem[];
}

// `update` payload: ProcessService.update accepts the same params shape as
// create (items + name/note); kept loose here because the operator Edit page
// reuses the create form layout and the backend permits the same fields.
export type InventoryTransformationUpdateInput = Partial<InventoryTransformationCreateInput>;

export const inventoryTransformationService = {
  suggestName: async (merchantId: string | null = null) => {
    const res = await operatorApiFetch('inventory_transformations/suggest_name', {
      method: 'GET',
      params: { merchant_id: merchantId },
    });
    return parseApiResponse(res, apiSuccess(SuggestNameSchema));
  },

  // Param typed as `Record<string, unknown>` (not the strict
  // `InventoryTransformationCreateInput` interface above) because the operator
  // New / Modal pages construct the payload from Ant Design form state and
  // can't statically prove every required field is set at the call site.
  // The interface is exported for callers that opt into strict typing.
  //
  // body 接的 `JsonObject | Record<string, unknown>` 都通：typed Input 走前者
  // （`extends JsonObject`），dynamic Hash 走後者，不再需要 cast。
  create: async (data: InventoryTransformationCreateInput | Record<string, unknown>) => {
    const res = await operatorApiFetch('inventory_transformations', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(InventoryTransformationCreatedSchema));
  },

  getRecords: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('inventory_transformations/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(InventoryTransformationRowSchema));
  },

  get: async (id: string) => {
    const res = await operatorApiFetch(`inventory_transformations/${id}`);
    return parseApiResponse(res, apiSuccess(InventoryTransformationDetailSchema));
  },

  approve: async (id: string) => {
    const res = await operatorApiFetch(`inventory_transformations/${id}/approve`, {
      method: 'POST',
    });
    return parseApiResponse(res, SuccessEnvelopeSchema);
  },

  reject: async (id: string) => {
    const res = await operatorApiFetch(`inventory_transformations/${id}/reject`, {
      method: 'POST',
    });
    return parseApiResponse(res, SuccessEnvelopeSchema);
  },

  update: async (id: string, data: InventoryTransformationUpdateInput | Record<string, unknown>) => {
    const res = await operatorApiFetch(`inventory_transformations/${id}`, {
      method: 'PATCH',
      body: data,
    });
    return parseApiResponse(res, SuccessEnvelopeSchema);
  },
};

export default inventoryTransformationService;

// z.infer types for callers that want to type their state
export type InventoryTransformationRow = z.infer<typeof InventoryTransformationRowSchema>;
export type InventoryTransformationCreated = z.infer<typeof InventoryTransformationCreatedSchema>;
